/**
 * Phase 23a — Daily Prompt card.
 *
 * Surfaces ONE admin-created prompt or poll that matches the viewer's
 * targeting (country/language/age/etc). Responds inline; dismiss to
 * skip. Hides itself until next server response if nothing matches.
 */
import { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface Prompt {
  id: string;
  type: 'prompt' | 'poll';
  body: string;
  options: string[] | null;
}

export default function DailyPromptCard() {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/me/daily-prompt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setPrompt(data.prompt);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = useCallback(async (payload: { answer?: number; answer_text?: string }) => {
    if (!prompt || submitting) return;
    setSubmitting(true);
    const token = localStorage.getItem('authToken');
    try {
      await fetch(`${API_URL}/me/daily-prompt/${prompt.id}/respond`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch { /* noop */ }
    setPrompt(null);
    setAnswerText('');
    setSubmitting(false);
  }, [prompt, submitting]);

  const dismiss = useCallback(async () => {
    if (!prompt) return;
    const token = localStorage.getItem('authToken');
    try {
      await fetch(`${API_URL}/me/daily-prompt/${prompt.id}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* noop */ }
    setPrompt(null);
  }, [prompt]);

  if (!prompt) return null;

  return (
    <div className="mb-4 p-4 rounded-xl bg-linear-to-br/srgb from-iris-500/10 to-iris-500/10 border border-iris-500/30">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-iris-300 mb-1">
            Daily Prompt
          </div>
          <p dir="auto" className="text-[15px] text-white leading-snug">{prompt.body}</p>
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-md text-[#8e8ea0] hover:text-white hover:bg-white/5"
          title="Skip"
        >
          <X size={16} />
        </button>
      </div>
      {prompt.type === 'poll' && prompt.options ? (
        <div className="grid gap-1.5">
          {prompt.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => respond({ answer: idx })}
              disabled={submitting}
              dir="auto"
              className="px-3 py-2 rounded-lg bg-white/4 hover:bg-iris-500/20 text-[14px] text-white text-left transition-colors disabled:opacity-50"
            >
              {opt}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            dir="auto"
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && answerText.trim()) respond({ answer_text: answerText.trim() });
            }}
            placeholder="Your answer…"
            className="flex-1 px-3 py-2 rounded-lg bg-white/4 border border-white/6 text-[14px] text-white placeholder-[#9a9aac] focus:outline-hidden focus:border-iris-400/40"
          />
          <button
            onClick={() => respond({ answer_text: answerText.trim() })}
            disabled={submitting || !answerText.trim()}
            className="px-4 py-2 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-[13px] font-medium disabled:opacity-40"
          >Send</button>
        </div>
      )}
    </div>
  );
}
